/** Per-user AI Score / Paper Score for the Retriever Excel export.

The student cards already know these totals. The answer sheet only carries
per-answer figures (AI Assessment Score, answer_score), so these helpers lift
the same card rules to the submission level and put them on their own "Score"
sheet -- one row per submission instead of the same pair repeated down every
answer row -- dropping a column entirely when nobody has that score.
*/

export const AI_SCORE_COLUMN = 'AI Score';
export const PAPER_SCORE_COLUMN = 'Paper Score';
export const SCORE_SHEET_NAME = 'Score';
export const AI_SCORE_SHEET_NAME = 'AI_Score';
export const MARKS_SHEET_NAME = 'Marks';

/**
 * {earned, max} as NUMBERS, or null when the student has no marks. The Marks
 * sheet needs the two apart -- the max is a property of the LOCALE, identical
 * for every student, so it belongs in the column header, not repeated down
 * every cell.
 */
export function readPaperScorePartsFromSubmissionRow(row) {
  if (!row) return null;
  const rawScore = row.score !== undefined && row.score !== null ? row.score : row.score_value;
  const rawMax = row.score_max !== undefined && row.score_max !== null ? row.score_max : row.max_score;
  const scoreNum = (rawScore !== undefined && rawScore !== null && rawScore !== '') ? parseFloat(rawScore) : null;
  const maxNum = (rawMax !== undefined && rawMax !== null && rawMax !== '') ? parseFloat(rawMax) : null;
  if (scoreNum === null || isNaN(scoreNum) || scoreNum < 0) return null;
  if (maxNum === null || isNaN(maxNum) || maxNum < 0) return null;
  return { earned: scoreNum, max: maxNum };
}

export function readPaperScoreFromSubmissionRow(row) {
  return formatPaperScore(readPaperScorePartsFromSubmissionRow(row));
}

export function computePaperScorePartsFromHotspotList(hotspotList) {
  let earned = 0;
  let max = 0;
  let hasMarkedComponent = false;
  let hasScoredAnswer = false;
  (Array.isArray(hotspotList) ? hotspotList : []).forEach((hotspot) => {
    const components = (hotspot && hotspot.hotspot_detail && Array.isArray(hotspot.hotspot_detail.component_list))
      ? hotspot.hotspot_detail.component_list
      : [];
    components.forEach((component) => {
      if (!component) return;
      const componentMax = parseFloat(component.score_max);
      if (!isNaN(componentMax) && componentMax >= 0) {
        hasMarkedComponent = true;
        max += componentMax;
      }
    });
    const answers = (hotspot && Array.isArray(hotspot.answer_list)) ? hotspot.answer_list : [];
    answers.forEach((answer) => {
      if (!answer) return;
      const answerScore = parseFloat(answer.score);
      if (!isNaN(answerScore) && answerScore >= 0) {
        hasScoredAnswer = true;
        earned += answerScore;
      }
    });
  });
  if (!hasMarkedComponent || !hasScoredAnswer) return null;
  return { earned, max };
}

export function computePaperScoreFromHotspotList(hotspotList) {
  return formatPaperScore(computePaperScorePartsFromHotspotList(hotspotList));
}

/** The `12/20` form the "Score" sheet and the student cards use. */
export function formatPaperScore(parts) {
  return parts ? `${parts.earned}/${parts.max}` : '';
}

/**
 * One row of the "Score" sheet. The key order IS the header order --
 * XLSX.utils.json_to_sheet takes the columns in insertion order -- so keep it
 * as username | udid | submission_id | submission_time | AI Score | Paper Score.
 */
export function buildUserScoreRow({ username, udid, submissionId, submissionTime, aiScore, paperScore }) {
  const text = (value) => (value === undefined || value === null ? '' : value);
  return {
    username: text(username),
    udid: text(udid),
    submission_id: text(submissionId),
    submission_time: text(submissionTime),
    [AI_SCORE_COLUMN]: text(aiScore),
    [PAPER_SCORE_COLUMN]: text(paperScore),
  };
}

/**
 * Whether the sheet is worth writing at all. With no AI assessments AND no
 * marks, omitEmptyUserScoreColumns strips both score columns and what is left
 * is a tab of bare identity columns -- better to skip it.
 */
export function hasAnyUserScore(rows) {
  return (Array.isArray(rows) ? rows : []).some(
    (row) => row && (row[AI_SCORE_COLUMN] || row[PAPER_SCORE_COLUMN])
  );
}

const blank = (value) => (value === undefined || value === null ? '' : value);

const hasAiPercent = (hotspot) => hotspot
  && hotspot.aiPercent !== null && hotspot.aiPercent !== undefined && !isNaN(hotspot.aiPercent);

const carriesMarks = (hotspot) => hotspot && hotspot.max > 0;

/**
 * The hotspot columns of a per-hotspot sheet, in epack order.
 *
 * Keyed by hotspot id, not name: two hotspots may share a name, and a sheet
 * cannot carry two identical headers (json_to_sheet keeps only the last).
 * Every submission carries the WHOLE epack's hotspots, so the union across
 * students is the epack -- taking the union anyway means one student's empty
 * shell cannot silently shorten the sheet. A hotspot nobody has a figure for is
 * dropped: an all-blank column says nothing.
 */
function collectHotspotColumns(entries, hasValue) {
  const order = [];
  const nameById = new Map();
  entries.forEach((entry) => {
    ((entry && entry.breakdown) || []).forEach((hotspot) => {
      if (!hotspot || hotspot.id === undefined || hotspot.id === null) return;
      const key = String(hotspot.id);
      if (!nameById.has(key)) {
        nameById.set(key, hotspot.name || '');
        order.push(key);
      } else if (!nameById.get(key) && hotspot.name) {
        nameById.set(key, hotspot.name);
      }
    });
  });

  const used = new Set();
  return order
    .filter((key) => entries.some((entry) => ((entry && entry.breakdown) || [])
      .some((hotspot) => hotspot && String(hotspot.id) === key && hasValue(hotspot))))
    .map((key) => {
      const name = String(nameById.get(key) || '').trim() || `Hotspot ${key}`;
      // Uniqueness is settled on the NAME, before any suffix a caller appends:
      // two same-named hotspots can also carry the same marks total, so
      // "Museum (10)" twice would still collide.
      let base = name;
      let attempt = 2;
      while (used.has(base)) {
        base = attempt === 2 ? `${name} #${key}` : `${name} #${key} ${attempt}`;
        attempt += 1;
      }
      used.add(base);
      return { key, base };
    });
}

function indexBreakdown(breakdown) {
  const map = {};
  (Array.isArray(breakdown) ? breakdown : []).forEach((hotspot) => {
    if (hotspot && hotspot.id !== undefined && hotspot.id !== null) {
      map[String(hotspot.id)] = hotspot;
    }
  });
  return map;
}

const IDENTITY_COLUMNS = ['udid', 'username', 'submission_id'];

const identityOf = (entry) => ({
  udid: blank(entry && entry.udid),
  username: blank(entry && entry.username),
  submission_id: blank(entry && entry.submissionId),
});

/** `Paper Score (63)` -- the out-of total, stated once. */
function withMax(label, max) {
  return (max === null || max === undefined || isNaN(max)) ? label : `${label} (${max})`;
}

/**
 * The out-of total for one hotspot column. It is a property of the epack, so
 * every student reports the same number; the largest is taken only so that a
 * student whose tree came back short cannot shrink the header.
 */
function hotspotMax(entries, key) {
  let max = null;
  entries.forEach((entry) => ((entry && entry.breakdown) || []).forEach((hotspot) => {
    if (hotspot && String(hotspot.id) === key && carriesMarks(hotspot)
        && (max === null || hotspot.max > max)) {
      max = hotspot.max;
    }
  }));
  return max;
}

/** udid | username | submission_id | AI Score | <each hotspot's AI Score>. */
export function buildAiScoreRows(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const columns = collectHotspotColumns(list, hasAiPercent);
  return list.map((entry) => {
    // Key order IS header order. A percentage has no out-of total to lift into
    // the header, so these columns stay plain.
    const row = { ...identityOf(entry), [AI_SCORE_COLUMN]: blank(entry && entry.aiScore) };
    const byId = indexBreakdown(entry && entry.breakdown);
    columns.forEach(({ key, base }) => {
      const hotspot = byId[key];
      row[base] = hasAiPercent(hotspot) ? `${hotspot.aiPercent}%` : '';
    });
    return row;
  });
}

/**
 * udid | username | submission_id | Paper Score (63) | <hotspot> (10) | ...
 *
 * Marks earned only, as NUMBERS -- the out-of total is identical for every
 * student, so it is stated once in the header instead of on all 63 rows, and
 * the cells stay summable and sortable in Excel.
 */
export function buildMarksRows(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const columns = collectHotspotColumns(list, carriesMarks)
    .map((column) => ({ ...column, label: withMax(column.base, hotspotMax(list, column.key)) }));

  let paperMax = null;
  list.forEach((entry) => {
    const parts = entry && entry.paperParts;
    if (parts && (paperMax === null || parts.max > paperMax)) paperMax = parts.max;
  });
  const paperColumn = withMax(PAPER_SCORE_COLUMN, paperMax);

  return list.map((entry) => {
    const parts = entry && entry.paperParts;
    const row = { ...identityOf(entry), [paperColumn]: parts ? parts.earned : '' };
    const byId = indexBreakdown(entry && entry.breakdown);
    columns.forEach(({ key, label }) => {
      const hotspot = byId[key];
      row[label] = carriesMarks(hotspot) ? hotspot.earned : '';
    });
    return row;
  });
}

/**
 * Whether the sheet carries anything beyond identity columns -- i.e. whether
 * downloading it would tell the teacher anything. A locale with no AI
 * assessments (or no marks) produces rows of nothing but udid/username/id.
 */
export function hasExportableScores(rows) {
  // Blank, not falsy: a whole class scoring 0 marks is a real, exportable
  // result, and 0 is the value the Marks sheet writes for it.
  return (Array.isArray(rows) ? rows : []).some((row) => row && Object.keys(row)
    .some((key) => IDENTITY_COLUMNS.indexOf(key) === -1
      && row[key] !== '' && row[key] !== null && row[key] !== undefined));
}

export function buildScoreBySubmissionId(list) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((item) => {
    if (!item || item.id == null) return;
    map[String(item.id)] = item;
  });
  return map;
}

export function omitEmptyUserScoreColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return data;
  const hasAi = data.some((row) => row && row[AI_SCORE_COLUMN]);
  const hasPaper = data.some((row) => row && row[PAPER_SCORE_COLUMN]);
  if (hasAi && hasPaper) return data;
  return data.map((row) => {
    if (!row) return row;
    const next = { ...row };
    if (!hasAi) delete next[AI_SCORE_COLUMN];
    if (!hasPaper) delete next[PAPER_SCORE_COLUMN];
    return next;
  });
}
